import { Button, Modal, Tabs } from "@mantine/core";
import { useMatch, useNavigate, useResolvedPath } from "react-router";
import { usePersons } from "../../../hooks/usePersons";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import type {
  ExtendedUser,
  Priority,
  Todo
} from "../../../utils/backend-types";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import { Mode } from "../Todos.types";
import type { TodoStatusCellProps } from "../TodoTable/TableColumns";
import { Notes } from "./Notes/Notes";
import { DescriptionTab } from "./Tabs/DescriptionTab";
import { LogsTab } from "./Tabs/LogsTab";
import { OverviewTab } from "./Tabs/OverviewTab";
import "./TodoModal.scss";

export const TABS = {
  DOCUMENTS: "dokumente",
  DESCRIPTION: "beschreibung",
  NOTES: "notizen",
  LOGS: "historie"
} as const;

interface TodoModalProps extends Pick<TodoStatusCellProps, "onChangeStatus"> {
  isOpen: boolean;
  mode: Mode;
  todo: Todo;
  users: Array<ExtendedUser>;
  variantId: number;
  showTodoStatus?: boolean;
  onToggle: () => void;
  onOpenEditTodoModal: () => void;
  onChangeResponsible: (responsibleId: number | null, todoId: number) => void;
  onChangePriority: (priorityValue: Priority, todoId: number) => void;
  onChangeTodoStatus: (todoId: number) => void;
  onChangeDueDate: (todoId: number, dueDate: string | null) => void;
  onChangeOverdue: (todoId: number, overdue: boolean) => void;
  onChangePerson: (todoId: number, person: number) => void;
}

function TodoModal({
  isOpen,
  mode,
  todo: {
    id,
    label,
    description,
    regulatoryDuty,
    category,
    priority: priorityValue,
    fulfillFor,
    person: personName,
    dueDate,
    overdue,
    responsible: responsibleId,
    status,
    todoLogs: logs,
    todoFiles: documents,
    documentProvided
  },
  users,
  variantId,
  showTodoStatus,
  onToggle,
  onOpenEditTodoModal,
  onChangeStatus,
  onChangeResponsible,
  onChangePriority,
  onChangeTodoStatus,
  onChangeDueDate,
  onChangeOverdue,
  onChangePerson
}: TodoModalProps) {
  const navigate = useNavigate();
  const resolved = useResolvedPath(":activeTab");
  const match = useMatch({ path: resolved.pathname, end: true });
  const activeTab =
    match && match.params ? match.params.activeTab : TABS.DOCUMENTS;
  const { shouldShowStaffView } = useShouldShowStaffView();

  const {
    persons,
    isLoading: personsLoading,
    error: personsError
  } = usePersons({ variantId });

  const universal = mode === Mode.Universal;
  const canEdit = mode === Mode.Normal && shouldShowStaffView;

  return (
    <Modal
      className="TodoModal"
      opened={isOpen}
      size="min(80vw, 1200px)"
      title={label}
      onClose={onToggle}
    >
      <Tabs value={activeTab} onChange={(newTab) => navigate(`${newTab}`)}>
        <Tabs.List>
          <Tabs.Tab component="a" value={TABS.DOCUMENTS}>
            Dokumente
          </Tabs.Tab>
          <Tabs.Tab component="a" value={TABS.DESCRIPTION}>
            Beschreibung
          </Tabs.Tab>
          <Tabs.Tab component="a" value={TABS.NOTES}>
            Notizen
          </Tabs.Tab>
          <Tabs.Tab component="a" value={TABS.LOGS}>
            Historie
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={TABS.DOCUMENTS}>
          <OverviewTab
            documents={documents}
            mode={mode}
            regulatoryDuty={regulatoryDuty}
            showTodoStatus={showTodoStatus}
            todoCategory={category}
            todoId={id}
            todoLabel={label}
            variantId={variantId}
          />
        </Tabs.Panel>
        <Tabs.Panel value={TABS.DESCRIPTION}>
          <LoadOrError error={personsError} loading={personsLoading}>
            <DescriptionTab
              category={category}
              description={description}
              dueDate={dueDate}
              fulfillFor={fulfillFor}
              label={label}
              overdue={overdue}
              personName={personName}
              persons={persons}
              priorityValue={priorityValue}
              regulatoryDuty={regulatoryDuty}
              responsibleId={responsibleId}
              status={status}
              todoId={id}
              todoStatus={documentProvided}
              universal={universal}
              users={users}
              onChangeDueDate={onChangeDueDate}
              onChangeOverdue={onChangeOverdue}
              onChangePerson={onChangePerson}
              onChangePriority={onChangePriority}
              onChangeResponsible={onChangeResponsible}
              onChangeStatus={onChangeStatus}
              onChangeTodoStatus={onChangeTodoStatus}
            />
          </LoadOrError>
        </Tabs.Panel>
        <Tabs.Panel value={TABS.NOTES}>
          <Notes todoId={id} />
        </Tabs.Panel>
        <Tabs.Panel value={TABS.LOGS}>
          <LogsTab logs={logs} />
        </Tabs.Panel>
      </Tabs>
      <ModalFooter justify="space-between">
        <Button variant="default" onClick={onToggle}>
          Schließen
        </Button>
        {canEdit && <Button onClick={onOpenEditTodoModal}>Bearbeiten</Button>}
      </ModalFooter>
    </Modal>
  );
}

export { TodoModal };
